import React, {useCallback, useContext, useEffect, useState} from "react";
import UiContext from "../../../contexts/UiContext";
import {EmblaOptionsType} from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import "./OwCarousel.scss";

type Props = {
    carouselImages: {src: string}[]
};

export const OwCarousel = ({carouselImages}: Props) => {
    // Start at the current attachment index if available
    const startIndex = 0;

    // Embla carousel options
    const OPTIONS: EmblaOptionsType = {
        containScroll: "trimSnaps",
        loop: true,
        align: "center",
        slidesToScroll: 1
    };

    const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS);
    const uiContext = useContext(UiContext);

    // Carousel state
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<any[]>([]);

    // Setup carousel event listeners
    useEffect(() => {
        if (!emblaApi) return;

        console.log('Embla API initialized:', emblaApi);

        const onSelect = () => {
            const newIndex = emblaApi.selectedScrollSnap();
            console.log('Embla selected index changed to:', newIndex);
            setSelectedIndex(newIndex);
        };

        emblaApi.on("select", onSelect);

        try {
            const snapList = emblaApi.scrollSnapList();
            console.log('Embla snap list:', snapList.length);
            setScrollSnaps(snapList);
        } catch (err) {
            console.error('Error getting snap list:', err);
        }

        // Set initial index
        onSelect();

        // Keyboard navigation handler
        const handleKeyDown = (e) => {
            if (e.key === "ArrowLeft") {
                console.log('Left arrow pressed');
                emblaApi.scrollPrev();
            } else if (e.key === "ArrowRight") {
                console.log('Right arrow pressed');
                emblaApi.scrollNext();
            }
        };

        // Only add keyboard listener when modal is open
        if (uiContext.modalIsOpen) {
            window.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            console.log('Cleaning up embla event listeners');
            emblaApi.off("select", onSelect);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [emblaApi, uiContext.modalIsOpen]);

    // Carousel navigation functions
    const scrollPrev = useCallback(() => {
        if (emblaApi) {
            console.log('Scrolling to previous slide');
            emblaApi.scrollPrev();
        }
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        if (emblaApi) {
            console.log('Scrolling to next slide');
            emblaApi.scrollNext();
        }
    }, [emblaApi]);

    const scrollTo = useCallback((index) => {
        if (emblaApi) {
            console.log('Scrolling to index:', index);
            emblaApi.scrollTo(index);
        }
    }, [emblaApi]);

    // if (emblaApi && emblaApi.canScrollNext()) {
    //     emblaApi.scrollTo(startIndex);
    // }

    return <div className={"OwCarousel"}>
        <div className="embla-container">
            <div className="embla">
                <div className="embla__viewport" ref={emblaRef}>
                    <div className="embla__container">
                        {
                            carouselImages.map((imageDetail, index) => (
                                <div key={index} className="embla__slide">
                                    <img src={imageDetail.src} alt={`Image ${index + 1}`} />
                                </div>
                            ))
                        }
                    </div>
                </div>

                {/* Navigation arrows - only show if multiple images */}
                {carouselImages.length > 1 && (
                    <>
                        <button
                            className="embla__prev"
                            onClick={scrollPrev}
                            type="button"
                            aria-label="Previous slide"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                            </svg>
                        </button>
                        <button
                            className="embla__next"
                            onClick={scrollNext}
                            type="button"
                            aria-label="Next slide"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
                            </svg>
                        </button>

                        {/* Indicator dots */}
                        <div className="embla__dots">
                            {carouselImages.map((_, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className={`embla__dot ${index === selectedIndex ? "embla__dot--selected" : ""}`}
                                    onClick={() => scrollTo(index)}
                                    aria-label={`Go to slide ${index + 1}`}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Close button */}
            <button
                className="embla__close"
                onClick={() => uiContext.showModal(false)}
                type="button"
                aria-label="Close image viewer"
            >
                <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
            </button>
        </div>
    </div>;
};
